import React, { useId } from 'react';
import { formatDateWeekdayDayMonth } from '../../utils/dateFormat';
import { calendarDayGap } from '../../utils/hireCost';

/**
 * "WHEN DID IT COME BACK?" — the off-hire return-date chooser (owner ruling
 * 2026-10-19, U2, from a live mockup). Three selectable rows replace the
 * full-width date picker, its "Defaults to today…" hint, the "overdue by N
 * days" line and the "Booked X to Y · returning Z" line:
 *   · As booked — <hire_to>   DEFAULT, green "On time". `hire_to` is the LAST
 *                             hired day, so returning on it is on time.
 *   · Today — <today>         red "N days late", or green "On time"/"N days early".
 *   · Another date            a small date box inside the row, shown when selected.
 * Late/early is counted against `hire_to` (`calendarDayGap`, the overrun's
 * own interval). Shared by `HireActionModal` and `OffHireAllDialog` — never copy.
 *
 * ⚖️ PRESELECTION, three rulings reconciled (coordinator-accepted 2026-10-19):
 *   1. U2 (2026-10-19): "As booked" is selected on open.
 *   2. 2026-08-31 (Jake Dimmer's report): a date ALREADY RECORDED on the item
 *      (`actual_return_date`) must show on reopen, or it reads as "not saved".
 *   3. 2026-10-05 option A: a Gantt drag that shortens to a PASSED date opens
 *      this pane pre-filled with the dragged date (`initialReturnDate`).
 * So a preset date (2 or 3) selects the row it matches — As booked, Today, or
 * Another date holding it — and only with no preset does rule 1 apply.
 */

export const RETURN_AS_BOOKED = 'booked';
export const RETURN_TODAY = 'today';
export const RETURN_OTHER = 'other';

/** The chooser's opening state. `hireTo` null → no "As booked" row (Today instead). */
export function initialReturnDateState({ hireTo, presetDate, today }) {
  if (presetDate) {
    if (hireTo && presetDate === hireTo) return { mode: RETURN_AS_BOOKED, other: presetDate };
    if (presetDate === today) return { mode: RETURN_TODAY, other: presetDate };
    return { mode: RETURN_OTHER, other: presetDate };
  }
  return hireTo ? { mode: RETURN_AS_BOOKED, other: '' } : { mode: RETURN_TODAY, other: '' };
}

/** The date a chooser state stands for (`''` when "Another date" is still empty). */
export function returnDateOf(state, hireTo, today) {
  if (state.mode === RETURN_AS_BOOKED && hireTo) return hireTo;
  if (state.mode === RETURN_OTHER) return state.other || '';
  return today;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** `{ kind: 'on-time'|'late'|'early', text }` for a return against `hire_to`; `null` when unknowable. */
export function returnTiming(hireTo, date) {
  const gap = calendarDayGap(hireTo, date);
  if (gap === null) return null;
  if (gap === 0) return { kind: 'on-time', text: 'On time' };
  return gap > 0
    ? { kind: 'late', text: `${plural(gap, 'day')} late` }
    : { kind: 'early', text: `${plural(-gap, 'day')} early` };
}

/** "Fri 10 Oct 2026" — weekday dd Mon yyyy. */
export function formatReturnDate(iso) {
  if (!iso) return '';
  return `${formatDateWeekdayDayMonth(iso)} ${String(iso).slice(0, 4)}`;
}

const RADIO = 'h-4 w-4 shrink-0 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500';
// Late is red; on time and early are green — never colour alone: the words say it.
const TIMING_TONE = {
  late: 'text-red-600 dark:text-red-400',
  'on-time': 'text-green-700 dark:text-green-400',
  early: 'text-green-700 dark:text-green-400',
};

function TimingBadge({ timing, testId }) {
  if (!timing) return null;
  return (
    <span className={`text-xs font-medium whitespace-nowrap ${TIMING_TONE[timing.kind]}`} data-testid={testId}>
      {timing.text}
    </span>
  );
}

/**
 * CONTROLLED. `state` is `{ mode, other }`; `onChange(nextState)`. `tid(part)`
 * maps a part name to the dialog's test id ('return-date' is the date box).
 */
export default function OffHireReturnDateChoice({ hireTo, today, state, onChange, tid }) {
  const name = useId();
  const rows = [
    hireTo && { mode: RETURN_AS_BOOKED, label: `As booked — ${formatReturnDate(hireTo)}`, date: hireTo },
    { mode: RETURN_TODAY, label: `Today — ${formatReturnDate(today)}`, date: today },
    { mode: RETURN_OTHER, label: 'Another date', date: state.other },
  ].filter(Boolean);

  const select = (mode) => {
    if (mode === state.mode) return;
    // "Another date" opens holding the date in force, so its box is never empty.
    const other = mode === RETURN_OTHER && !state.other ? returnDateOf(state, hireTo, today) : state.other;
    onChange({ mode, other });
  };

  return (
    <fieldset data-testid={tid('return-choice')}>
      <legend className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5">When did it come back?</legend>
      <div className="border rounded-lg border-gray-200 dark:border-gray-700 p-1 space-y-0.5">
        {rows.map((row) => {
          const checked = state.mode === row.mode;
          return (
            <label
              key={row.mode}
              className={`flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-3 py-2 rounded-md text-sm cursor-pointer text-gray-800 dark:text-gray-200 ${
                checked ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
              }`}
              data-testid={tid(`return-${row.mode}-row`)}
            >
              <input
                type="radio"
                name={name}
                value={row.mode}
                checked={checked}
                onChange={() => select(row.mode)}
                className={RADIO}
                data-testid={tid(`return-${row.mode}`)}
              />
              <span className={checked ? 'font-medium' : ''}>{row.label}</span>
              {row.mode === RETURN_OTHER && checked && (
                <input
                  type="date"
                  value={state.other}
                  onChange={(e) => onChange({ mode: RETURN_OTHER, other: e.target.value })}
                  aria-label="Return date"
                  className="px-2 py-1 border rounded-md text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                  data-testid={tid('return-date')}
                />
              )}
              <span className="ml-auto">
                <TimingBadge timing={hireTo && row.date ? returnTiming(hireTo, row.date) : null} testId={tid(`return-${row.mode}-timing`)} />
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
